import { Mutation } from "@apollo/client/react/components";
import { SearchAlgoliaIndex } from "@govlaunch/algolia";
import { Button, Modal, ModalClose } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { MultiGovernmentSelect } from "@govlaunch/select";
import { Box, CountryFlag, TCountryCode } from "@govlaunch/web";
import moment from "moment";
import Link from "next/link";
import React, { createRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useToggle } from "react-use";
import useClipboard from "~/lib/hooks/useClipboard";
import useShowMoreCounter from "~/lib/hooks/useShowMoreCounter";
import CancelGroupRequest from "~/lib/mutations/CancelGroupRequest";
import InviteGovernmentsToGroup from "~/lib/mutations/InviteGovernmentsToGroup";
import {
  ICancelGroupRequestMutation,
  ICancelGroupRequestMutationVariables,
} from "~/lib/mutations/__generated__/CancelGroupRequest.generated";
import {
  IInviteGovernmentsToGroupMutation,
  IInviteGovernmentsToGroupMutationVariables,
} from "~/lib/mutations/__generated__/InviteGovernmentsToGroup.generated";
import { IGroupQuery } from "~/lib/queries/__generated__/GroupQuery.generated";
import filterByTypename from "~/lib/utils/filterByTypename";
import getGovlaunchBaseUrl from "~/lib/utils/getGovlaunchBaseUrl";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import { IGovernment, IGroup } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import ClipboardIcon from "~/components/icons/ClipboardIcon";
import mediaQueries from "~/components/mediaQueries";
import { Margin } from "~/components/spacings";
import Tooltip from "~/components/Tooltip";

interface IGroupGovernmentsProps {
  group: Pick<IGroup, "_id" | "name" | "slug" | "viewerIsMember"> & {
    governments: Pick<IGovernment, "_id" | "name" | "slug" | "logo">[];
    pendingRequests: NonNullable<IGroupQuery["groupBySlug"]>["pendingRequests"];
  };
}

export default function GroupGovernments({ group }: IGroupGovernmentsProps) {
  const [isOpen, toggle] = useToggle(false);
  const [isShowingPendingInvites, togglePendingInvites] = useToggle(false);
  const { governments } = group;
  const { offset, showMore, hasMore } = useShowMoreCounter(governments.length, 18);
  const requests = group.pendingRequests.filter(filterByTypename("GovernmentGroupRequest"));

  if (governments.length === 0 && requests.length === 0 && !group.viewerIsMember) {
    return null;
  }

  return (
    <div>
      <GovernmentInvitationModal group={group} isOpen={isOpen} onRequestClose={toggle} />

      {governments.length === 0 && requests.length === 0 && group.viewerIsMember ? (
        <Button
          size="large"
          theme="secondary"
          color={palette.primary}
          onClick={toggle}
          css={{
            background: "#fff",
            width: "100%",
          }}
          data-intercom-target="Group Invite Governments Button"
        >
          <FormattedMessage defaultMessage="Invite Governments" id="ZiH9ot" />
        </Button>
      ) : (
        <div
          data-intercom-target="Group Governments Box"
          css={{
            borderRadius: 5,
            border: `1px solid ${palette.lightestGray}`,
            background: "#fff",
          }}
        >
          <div
            css={{
              padding: 16,
              display: "flex",
              borderBottom: `1px solid ${palette.lightestGray}`,
            }}
          >
            <span
              css={{
                fontSize: 12,
                fontWeight: 500,
                color: palette.sealBlue,
                textTransform: "uppercase",
              }}
            >
              <FormattedMessage defaultMessage="Governments" id="qnQX9U" />{" "}
              {group.governments.length > 0 && `(${group.governments.length})`}
            </span>

            {group.viewerIsMember && (
              <span
                role="button"
                css={{
                  marginLeft: "auto",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  color: palette.primary,
                  cursor: "pointer",
                  fontSize: 14,
                }}
                onClick={toggle}
                data-intercom-target="Group Invite Governments Button"
              >
                <FormattedMessage defaultMessage="Invite" id="hYOE+U" />
              </span>
            )}
          </div>

          <div>
            <div
              css={{
                padding: 16,
              }}
            >
              {governments.length > 0 && (
                <div
                  css={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 1fr)",
                    gridGap: 12,
                  }}
                >
                  {governments.slice(0, offset).map((government) => (
                    <Tooltip
                      key={government._id}
                      backgroundColor={palette.innovatorBlue}
                      boxStyle={{
                        padding: "4px 8px",
                        border: "none",
                        borderRadius: 4,
                        boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                      }}
                      arrowSize={8}
                      render={() => (
                        <span
                          css={{
                            color: palette.white,
                            fontWeight: 500,
                            fontSize: 13,
                            lineHeight: 1.33,
                            letterSpacing: 1,
                          }}
                        >
                          {government.name}
                        </span>
                      )}
                    >
                      {({ innerRef, bind }) => (
                        <Link
                          href="/governments/[governmentSlug]/[[...content]]"
                          as={`/governments/${government.slug}`}
                          passHref={true}
                          legacyBehavior={true}
                        >
                          <a
                            {...bind}
                            ref={innerRef}
                            css={{
                              position: "relative",
                              display: "block",
                              color: "unset",
                              textDecoration: "none",
                            }}
                          >
                            <ProfilePicture
                              size={42}
                              image={government.logo}
                              name={government.name}
                              css={{
                                display: "block",
                              }}
                            />
                          </a>
                        </Link>
                      )}
                    </Tooltip>
                  ))}
                </div>
              )}

              {requests.length > 0 && group.viewerIsMember && (
                <>
                  <span
                    role="button"
                    onClick={togglePendingInvites}
                    css={{
                      display: "block",
                      marginTop: group.governments.length > 0 ? 16 : 0,
                      cursor: "pointer",
                      fontWeight: 500,
                      color: palette.red,
                      fontSize: 14,
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="{count} pending {count, plural, one {invite} other {invites}}"
                      id="nd0Lhk"
                      values={{ count: requests.length }}
                    />
                  </span>
                  {isShowingPendingInvites && (
                    <Modal ariaHideApp={false} isOpen={true} onRequestClose={togglePendingInvites}>
                      <ModalClose onClick={togglePendingInvites} />
                      <div
                        css={mediaQueries({
                          width: [350, 350, 580, 580],
                          padding: 32,
                        })}
                      >
                        <PendingInvites group={group} groupRequests={requests} />
                      </div>
                    </Modal>
                  )}
                </>
              )}
            </div>

            {hasMore && (
              <button
                type="button"
                onClick={showMore}
                css={{
                  width: "100%",
                  background: "none",
                  outline: 0,
                  fontSize: 14,
                  fontWeight: 500,
                  height: 48,
                  textAlign: "center",
                  textTransform: "uppercase",
                  color: palette.sealBlue,
                  border: 0,
                  display: "block",
                  lineHeight: "48px",
                  borderTop: `1px solid ${palette.lightestGray}`,
                  cursor: "pointer",
                }}
              >
                Show More
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface IGovernmentInvitationModalProps {
  group: Pick<IGroup, "_id" | "name"> & {
    governments: Pick<IGovernment, "_id" | "name" | "slug" | "logo">[];
    pendingRequests: NonNullable<IGroupQuery["groupBySlug"]>["pendingRequests"];
  };
  isOpen: boolean;
  onRequestClose: (arg?: any) => void;
}

function GovernmentInvitationModal({ group, isOpen, onRequestClose }: IGovernmentInvitationModalProps) {
  const intl = useIntl();
  const [selectedGovernments, setSelectedGovernments] = useState([]);

  if (!isOpen) {
    return null;
  }

  return (
    <Mutation<IInviteGovernmentsToGroupMutation, IInviteGovernmentsToGroupMutationVariables>
      mutation={InviteGovernmentsToGroup}
    >
      {(inviteGovernments, { data }) => (
        <Modal
          ariaHideApp={false}
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          destroyOnClose={true}
          overlayStyle={{
            zIndex: 1000,
          }}
          style={{
            overflow: "visible",
          }}
        >
          <ModalClose onClick={onRequestClose} />

          {!(data && data.inviteGovernmentsToGroup) ? (
            <div
              css={mediaQueries({
                width: [350, 350, 650, 650],
                padding: 32,
              })}
            >
              <div>
                <div>
                  <p
                    css={{
                      fontSize: 18,
                      fontWeight: "bold",
                      color: palette.darkGray,
                      margin: "0 0 4px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span css={{ marginRight: 4 }}>
                      <FormattedMessage defaultMessage="Invite Governments" id="ZiH9ot" />
                    </span>
                    <svg width={20} height={20} viewBox="0 -1 512 511">
                      <path d="M73.766 211.945h49.703v201.97H73.766zm0 0" fill="#e6f1fa" />
                      <path d="M101.469 211.945h22v201.97h-22zm0 0" fill="#cee4f7" />
                      <path
                        d="M471.367 157.102L259.414 10.363a5.998 5.998 0 0 0-6.828 0L40.636 157.102v16.566h430.731zm0 0"
                        fill="#e6f1fa"
                      />
                      <path
                        d="M259.414 10.363a5.998 5.998 0 0 0-6.828 0L245 15.617l204.367 141.485v16.566h22v-16.566zm0 0"
                        fill="#cee4f7"
                      />
                      <path
                        d="M272.566 107.402c0 9.149-7.414 16.567-16.566 16.567-9.148 0-16.566-7.418-16.566-16.567 0-9.152 7.418-16.57 16.566-16.57 9.152 0 16.566 7.418 16.566 16.57zm0 0"
                        fill="#feda43"
                      />
                      <path
                        d="M134.035 223.367H63.2c-3.312 0-6-2.687-6-6v-37.922h82.836v37.922c0 3.313-2.687 6-6 6zm0 0"
                        fill="#537d91"
                      />
                      <path
                        d="M118.035 179.445v37.922c0 3.313-2.687 6-6 6h22c3.313 0 6-2.687 6-6v-37.922zm0 0"
                        fill="#45738a"
                      />
                      <path
                        d="M134.035 402.496H63.2c-3.312 0-6 2.684-6 6v37.918h82.836v-37.918c0-3.316-2.687-6-6-6zm0 0"
                        fill="#537d91"
                      />
                      <path
                        d="M118.035 446.414v-37.922c0-3.312-2.687-6-6-6h22c3.313 0 6 2.688 6 6v37.922zm0 0"
                        fill="#45738a"
                      />
                      <path d="M231.148 211.945h49.704v201.97h-49.704zm0 0" fill="#e6f1fa" />
                      <path d="M258.852 211.945h22v201.97h-22zm0 0" fill="#cee4f7" />
                      <path
                        d="M291.418 223.367h-70.832c-3.316 0-6-2.687-6-6v-37.922h82.832v37.922c0 3.313-2.688 6-6 6zm0 0"
                        fill="#537d91"
                      />
                      <path
                        d="M275.418 179.445v37.922c0 3.313-2.688 6-6 6h22c3.312 0 6-2.687 6-6v-37.922zm0 0"
                        fill="#45738a"
                      />
                      <path
                        d="M291.418 402.496h-70.832c-3.316 0-6 2.684-6 6v37.918h82.832v-37.918c0-3.316-2.688-6-6-6zm0 0"
                        fill="#537d91"
                      />
                      <path
                        d="M275.418 446.414v-37.922c0-3.312-2.688-6-6-6h22c3.312 0 6 2.688 6 6v37.922zm0 0"
                        fill="#45738a"
                      />
                      <path d="M388.531 211.945h49.703v201.97h-49.703zm0 0" fill="#e6f1fa" />
                      <path d="M416.234 211.945h22v201.97h-22zm0 0" fill="#cee4f7" />
                      <path
                        d="M448.8 223.367h-70.835c-3.313 0-6-2.687-6-6v-37.922H454.8v37.922c0 3.313-2.688 6-6 6zm0 0"
                        fill="#537d91"
                      />
                      <path
                        d="M432.8 179.445v37.922c0 3.313-2.687 6-6 6h22c3.313 0 6-2.687 6-6v-37.922zm0 0"
                        fill="#45738a"
                      />
                      <path
                        d="M448.8 402.496h-70.835c-3.313 0-6 2.684-6 6v37.918H454.8v-37.918c0-3.316-2.688-6-6-6zm0 0"
                        fill="#537d91"
                      />
                      <path
                        d="M432.8 446.414v-37.922c0-3.312-2.687-6-6-6h22c3.313 0 6 2.688 6 6v37.922zm0 0"
                        fill="#45738a"
                      />
                      <path
                        d="M481.934 190.234H30.066c-3.312 0-6-2.687-6-6v-21.132c0-3.317 2.688-6 6-6h451.868c3.312 0 6 2.683 6 6v21.132c0 3.313-2.688 6-6 6zm0 0"
                        fill="#feda43"
                      />
                      <path
                        d="M481.934 157.102h-22c3.312 0 6 2.683 6 6v21.132c0 3.313-2.688 6-6 6h22c3.312 0 6-2.687 6-6v-21.132c0-3.317-2.688-6-6-6zm0 0"
                        fill="#fdb53e"
                      />
                      <path
                        d="M487.934 485.328H24.066v-43.7c0-3.312 2.688-6 6-6h451.868c3.312 0 6 2.688 6 6zm0 0"
                        fill="#feda43"
                      />
                      <path
                        d="M481.934 435.629h-22c3.312 0 6 2.687 6 6v43.7h22v-43.7c0-3.313-2.688-6-6-6zm0 0"
                        fill="#fdb53e"
                      />
                      <path
                        d="M498.5 501.895h-485c-3.313 0-6-2.688-6-6v-21.133c0-3.313 2.688-6 6-6h485c3.313 0 6 2.687 6 6v21.133c0 3.312-2.688 6-6 6zm0 0"
                        fill="#e6f1fa"
                      />
                      <path
                        d="M498.5 468.762h-22c3.313 0 6 2.687 6 6v21.133c0 3.312-2.688 6-6 6h22c3.313 0 6-2.688 6-6v-21.133c0-3.313-2.688-6-6-6zm0 0"
                        fill="#cee4f7"
                      />
                      <path d="M256 131.469c13.273 0 24.066-10.797 24.066-24.067 0-13.273-10.796-24.07-24.066-24.07s-24.066 10.797-24.066 24.07c0 13.27 10.796 24.067 24.066 24.067zm0-33.137c5 0 9.066 4.07 9.066 9.07 0 4.996-4.066 9.067-9.066 9.067s-9.066-4.067-9.066-9.067 4.066-9.07 9.066-9.07zm0 0" />
                      <path d="M504.5 461.262h-9.066v-25.633c0-4.14-3.36-7.5-7.5-7.5H462.3v-25.633a7.5 7.5 0 0 0-7.5-7.5h-9.063V230.867h9.063a7.5 7.5 0 0 0 7.5-7.5v-25.633h25.633a7.5 7.5 0 0 0 7.5-7.5v-33.132a7.5 7.5 0 0 0-7.5-7.5H473.71L260.27 1.832a7.504 7.504 0 0 0-8.54 0L38.293 149.602H24.066a7.5 7.5 0 0 0-7.5 7.5v33.132c0 4.141 3.36 7.5 7.5 7.5H49.7v25.633c0 4.14 3.36 7.5 7.5 7.5h9.067v164.13h-9.067a7.5 7.5 0 0 0-7.5 7.5v25.632H24.066c-4.14 0-7.5 3.36-7.5 7.5v25.633H7.5a7.497 7.497 0 0 0-7.5 7.5v33.129c0 4.144 3.355 7.5 7.5 7.5h497c4.145 0 7.5-3.356 7.5-7.5v-33.13a7.5 7.5 0 0 0-7.5-7.5zm-57.2-33.133H379.47v-18.133H447.3zm-51.269-33.133V230.867h34.703v164.13zm51.27-179.129h-67.832v-18.133H447.3zm-75.332 15h9.062v164.13h-9.062a7.497 7.497 0 0 0-7.5 7.5v25.632h-59.551v-25.633a7.5 7.5 0 0 0-7.5-7.5h-9.066V230.867h9.066c4.14 0 7.5-3.36 7.5-7.5v-25.633h59.55v25.633a7.5 7.5 0 0 0 7.5 7.5zm-149.883-33.133h67.832v18.133h-67.832zm16.562 33.133h34.704v164.13h-34.704zm51.27 179.13v18.132h-67.832v-18.133zM31.566 164.601H162.45a7.5 7.5 0 0 0 7.5-7.5c0-4.145-3.355-7.5-7.5-7.5H64.645L256 17.122l191.36 132.48H192.45a7.5 7.5 0 1 0 0 15h287.984v18.132H31.566zM64.7 197.734h67.836v18.133H64.7zm51.27 33.133v164.13H81.266v-164.13zm-51.27 179.13h67.836v18.132H64.7zm75.336-15h-9.066v-164.13h9.066c4.14 0 7.5-3.36 7.5-7.5v-25.633h59.55v25.633a7.5 7.5 0 0 0 7.5 7.5h9.063v164.13h-9.062a7.497 7.497 0 0 0-7.5 7.5v25.632h-59.55v-25.633a7.5 7.5 0 0 0-7.5-7.5zM31.566 443.128h448.868v18.133H31.566zM497 494.395H15v-18.133h482zm0 0" />
                    </svg>
                  </p>
                  <p
                    css={{
                      fontSize: 14,
                      margin: 0,
                      color: palette.mediumGray,
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="They’ll be able to access {groupName}"
                      id="RBJjrh"
                      values={{ groupName: group.name }}
                    />
                  </p>
                </div>

                <Margin mt={28}>
                  <SearchAlgoliaIndex index="governments">
                    {({ results: governments, searchValue: inputValue, onChange: onInputValueChange }: any) => (
                      <MultiGovernmentSelect
                        selectableGovernments={governments.filter(({ _id }: { _id: string }) => {
                          return !group.governments.find((government) => government._id === _id);
                        })}
                        css={{
                          minWidth: 300,
                        }}
                        governments={selectedGovernments}
                        inputValue={inputValue}
                        onInputValueChange={onInputValueChange}
                        onChange={setSelectedGovernments}
                        placeholder={intl.formatMessage({ defaultMessage: "Search Governments", id: "uSbVfz" })}
                      />
                    )}
                  </SearchAlgoliaIndex>
                </Margin>

                <Margin mt={28}>
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      color={palette.innovatorBlue}
                      textColor={palette.white}
                      onClick={() => {
                        inviteGovernments({
                          variables: {
                            groupId: group._id,
                            governmentIds: selectedGovernments.map(({ objectID }) => objectID),
                          },
                        });
                      }}
                    >
                      <FormattedMessage defaultMessage="Invite Governments" id="ZiH9ot" />
                    </Button>
                    <Margin ml={8}>
                      <a
                        href="#"
                        onClick={onRequestClose}
                        css={{
                          color: palette.mediumGray,
                          fontSize: 14,
                        }}
                      >
                        <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                      </a>
                    </Margin>
                  </div>
                </Margin>
              </div>
            </div>
          ) : (
            <div
              css={mediaQueries({
                width: [350, 350, 580, 580],
                padding: 32,
              })}
            >
              <PendingInvites
                group={group}
                groupRequests={group.pendingRequests.filter(filterByTypename("GovernmentGroupRequest"))}
                onRequestClose={onRequestClose}
              />
            </div>
          )}
        </Modal>
      )}
    </Mutation>
  );
}

interface IPendingInvitesProps {
  group: Pick<IGroup, "_id" | "name">;
  groupRequests: NonNullable<IGroupQuery["groupBySlug"]>["pendingRequests"];
  onRequestClose?: (arg?: any) => void;
}

function PendingInvites({ group, groupRequests, onRequestClose }: IPendingInvitesProps) {
  if (!groupRequests) {
    return null;
  }

  const user = useSelfie();
  const inputRef = createRef<HTMLInputElement>();
  const [copied, setClipboard] = useClipboard(inputRef);

  if (!user) {
    return null;
  }

  return (
    <div>
      <h3
        css={{
          fontWeight: 600,
          fontSize: 18,
          color: palette.darkGray,
          margin: 0,
        }}
      >
        <FormattedMessage
          defaultMessage="Government Pending Invites ({count})"
          id="xvRsHS"
          values={{ count: groupRequests.length }}
        />
      </h3>

      <Mutation<ICancelGroupRequestMutation, ICancelGroupRequestMutationVariables> mutation={CancelGroupRequest}>
        {(cancelGroupRequest, { loading }) => (
          <Margin mt={24}>
            <div
              css={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, 70px)",
                gridGap: 32,
              }}
            >
              {groupRequests.map((groupRequest) => {
                if (groupRequest.__typename !== "GovernmentGroupRequest") {
                  return null;
                }

                const isSameHour = moment(groupRequest.createdAt).isSame(moment().toDate(), "hour");

                return (
                  <Tooltip
                    key={groupRequest._id}
                    backgroundColor={palette.innovatorBlue}
                    boxStyle={{
                      padding: "4px 8px",
                      border: "none",
                      borderRadius: 4,
                      boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                    }}
                    arrowSize={8}
                    render={() => (
                      <span
                        css={{
                          color: palette.white,
                          fontWeight: 500,
                          fontSize: 13,
                          lineHeight: 1.33,
                        }}
                      >
                        {groupRequest.government.name} - {groupRequest.government.city.countryName}
                      </span>
                    )}
                  >
                    {({ innerRef, bind }) => (
                      <Link
                        href="/governments/[governmentSlug]/[[...content]]"
                        as={`/governments/${groupRequest.government.slug}`}
                        passHref={true}
                        legacyBehavior={true}
                      >
                        <a
                          {...bind}
                          ref={innerRef}
                          css={{
                            position: "relative",
                            display: "block",
                            color: "unset",
                            textDecoration: "none",
                          }}
                        >
                          <div
                            role="button"
                            css={{
                              cursor: "pointer",
                              position: "absolute",
                              top: -5,
                              zIndex: 10,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "100%",
                              right: -5,
                              width: 22,
                              height: 22,
                              background: palette.red,
                            }}
                            onClick={(event) => {
                              event.preventDefault();

                              if (!loading) {
                                cancelGroupRequest({
                                  variables: {
                                    requestId: groupRequest._id,
                                  },
                                }).then(() => {
                                  if (groupRequests.length === 1 && onRequestClose) {
                                    onRequestClose();
                                  }
                                });
                              }
                            }}
                          >
                            <svg width={10} height={10}>
                              <path
                                fill="#FFF"
                                d="M6.188 5l3.566-3.565A.84.84 0 1 0 8.565.246L5 3.812 1.435.246A.84.84 0 1 0 .246 1.435L3.812 5 .246 8.565a.84.84 0 0 0 1.189 1.189L5 6.188l3.565 3.566a.84.84 0 1 0 1.189-1.189L6.188 5z"
                              />
                            </svg>
                          </div>

                          <Box position="relative">
                            <ProfilePicture
                              size={70}
                              image={groupRequest.government.logo}
                              name={groupRequest.government.name}
                              css={{
                                display: "block",
                              }}
                            />

                            <Box position="absolute" bottom={1} right={-3}>
                              <CountryFlag
                                countryCode={groupRequest.government.city.country as TCountryCode}
                                width={24}
                                height={14}
                              />
                            </Box>
                          </Box>

                          <span
                            css={{
                              display: "block",
                              marginTop: 2,
                              fontSize: 12,
                              color: palette.darkGray,
                              textAlign: "center",
                            }}
                          >
                            {isSameHour ? (
                              <FormattedMessage id="I90sbW" defaultMessage="just now" />
                            ) : (
                              <IntlRelativeTime value={groupRequest.createdAt} />
                            )}
                          </span>
                        </a>
                      </Link>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          </Margin>
        )}
      </Mutation>

      <Margin mt={24}>
        <div
          css={{
            fontWeight: 500,
            fontSize: 16,
            color: palette.darkGray,
          }}
        >
          <FormattedMessage
            defaultMessage="Send this invite link to your government contacts to join {group}"
            id="4Lf8mN"
            values={{ group: group.name }}
          />
        </div>
      </Margin>

      <Margin mt={24}>
        <input
          css={{
            boxSizing: "border-box",
            padding: 12,
            fontWeight: 500,
            fontSize: 14,
            color: palette.mediumGray,
            textAlign: "center",
            display: "block",
            fontFamily: "Courier",
            lineHeight: 1.44,
            backgroundColor: "transparent",
            border: `dashed 1px #48A0DA`,
            borderRadius: 4,
            outline: "none",
            WebkitAppearance: "none",
            width: "100%",
          }}
          value={`${getGovlaunchBaseUrl()}/signin?referral=${window.btoa(
            JSON.stringify({
              userId: user._id,
              groupId: group._id,
              type: "government-group-request",
            }),
          )}`}
          onChange={() => {}}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          readOnly={true}
          ref={inputRef}
        />
      </Margin>

      <Margin
        mt={16}
        css={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          css={{
            border: 0,
            outline: 0,
            background: "none",
            color: palette.primary,
            fontWeight: 500,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            padding: 0,
            margin: 0,
          }}
          type="button"
          onClick={() => {
            setClipboard();
          }}
        >
          <FormattedMessage defaultMessage="Copy invite link" id="rtACcE" />{" "}
          <span
            css={{
              marginLeft: 4,
            }}
          >
            <ClipboardIcon />
          </span>
        </button>

        {copied && (
          <span
            css={{
              marginLeft: 6,
              color: palette.greenSuccess,
            }}
          >
            <FormattedMessage defaultMessage="Copied!" id="0oL1zz" />
          </span>
        )}
      </Margin>
    </div>
  );
}
